import { useQuery } from '@tanstack/react-query';
import { STALE_TIME_DETAIL_MS } from '../../constants/reactQueryStaleTime.ts';
import type { ICareHomeListResponse } from '../../interfaces';
import type { ICareHomeStats } from '../../interfaces/CareHome/ICareHomeStats.ts';
import type { IClipListResponse } from '../../interfaces/Clip/IClipListResponse.ts';
import type { IGateway } from '../../interfaces/Gateway/IGateway.ts';
import CareHomeService from '../../services/CareHomeService.ts';
import ClipService from '../../services/ClipService.ts';
import GatewayService from '../../services/GatewayService.ts';

const HARDWARE_LIST_STALE_TIME_MS = Number.POSITIVE_INFINITY;
const HARDWARE_LIST_GC_TIME_MS = Number.POSITIVE_INFINITY;

export const getHardwareCareHomesQueryKey = (organisationId?: string) =>
  ['hardwareCareHomes', organisationId] as const;

export function useHardwareCareHomesQuery(organisationId?: string, enabled = true) {
  return useQuery<ICareHomeListResponse>({
    queryKey: getHardwareCareHomesQueryKey(organisationId),
    enabled: enabled && !!organisationId,
    staleTime: HARDWARE_LIST_STALE_TIME_MS,
    gcTime: HARDWARE_LIST_GC_TIME_MS,
    refetchOnMount: false,
    refetchOnReconnect: false,
    queryFn: () =>
      CareHomeService.getCareHomes({
        organisation_id: organisationId!,
      }),
  });
}

export const getHardwareCareHomeStatsQueryKey = (careHomeId?: string) =>
  ['hardwareCareHomeStats', careHomeId] as const;

export function useHardwareCareHomeStatsQuery(careHomeId?: string, enabled = true) {
  return useQuery<ICareHomeStats>({
    queryKey: getHardwareCareHomeStatsQueryKey(careHomeId),
    enabled: enabled && !!careHomeId,
    staleTime: STALE_TIME_DETAIL_MS,
    gcTime: HARDWARE_LIST_GC_TIME_MS,
    refetchOnMount: false,
    refetchOnReconnect: false,
    queryFn: () => CareHomeService.getCareHomeStats(careHomeId!),
  });
}

export type HardwareGatewaysListQueryInput = {
  page: number;
  perPage: number;
  tag: 'assigned' | 'free' | 'deactivated';
  query?: string;
  orderBy?: string;
  sortOrder?: 'asc' | 'desc';
  carehomeId?: string;
  organisationId?: string;
};

export const getHardwareGatewaysQueryKey = (input: HardwareGatewaysListQueryInput) =>
  [
    'hardwareGateways',
    input.organisationId,
    input.carehomeId,
    input.tag,
    input.page,
    input.perPage,
    input.query,
    input.orderBy,
    input.sortOrder,
  ] as const;

export function useHardwareGatewaysQuery(input: HardwareGatewaysListQueryInput, enabled = true) {
  return useQuery<{ items: IGateway[]; total: number }>({
    queryKey: getHardwareGatewaysQueryKey(input),
    enabled: enabled && !!input.tag,
    staleTime: HARDWARE_LIST_STALE_TIME_MS,
    gcTime: HARDWARE_LIST_GC_TIME_MS,
    refetchOnMount: false,
    refetchOnReconnect: false,
    queryFn: async () => {
      const params: Parameters<typeof GatewayService.getGateways>[0] = {
        page: input.page,
        per_page: input.perPage,
        type: 'Charger',
        carehome_id: input.carehomeId,
        organisation_id: input.organisationId,
        unassigned: input.tag === 'free' ? true : undefined,
        assigned: input.tag === 'assigned' ? true : undefined,
        query: input.query || undefined,
        is_active: input.tag === 'deactivated' ? false : true,
        orderBy: input.orderBy,
        sortOrder: input.sortOrder,
      };

      const response = await GatewayService.getGateways(params);
      return { items: response.items, total: response.total };
    },
  });
}

export type HardwareAvailableClipsQueryInput = {
  page: number;
  perPage: number;
  query?: string;
  orderBy?: string;
  sortOrder?: 'asc' | 'desc';
  carehomeId?: string;
  userRole?: string;
};

export const getHardwareAvailableClipsQueryKey = (input: HardwareAvailableClipsQueryInput) =>
  [
    'hardwareAvailableClips',
    input.carehomeId,
    input.page,
    input.perPage,
    input.query,
    input.orderBy,
    input.sortOrder,
  ] as const;

export function useHardwareAvailableClipsQuery(
  input: HardwareAvailableClipsQueryInput,
  enabled = true
) {
  return useQuery<IClipListResponse>({
    queryKey: getHardwareAvailableClipsQueryKey(input),
    enabled,
    staleTime: HARDWARE_LIST_STALE_TIME_MS,
    gcTime: HARDWARE_LIST_GC_TIME_MS,
    refetchOnMount: false,
    refetchOnReconnect: false,
    queryFn: () => {
      const params: Parameters<typeof ClipService.getClips>[0] = {
        page: input.page,
        per_page: input.perPage,
        is_active: true,
        unassigned: true,
        carehome_id: input.carehomeId,
      };

      if (input.query) {
        params.query = input.query;
        params.ignore_case = true;
      }

      if (input.orderBy && input.sortOrder) {
        params.orderBy = input.orderBy;
        params.sortOrder = input.sortOrder;
      }

      return ClipService.getClips(params);
    },
  });
}
